import { useEffect } from 'react';
import { App as CapApp } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import EventDetail from "./pages/EventDetail";
import EventDetailOwner from "./pages/EventDetailOwner";
import Analytics from "./pages/Analytics";
import GlobalAnalytics from "./pages/GlobalAnalytics";
import Profile from "./pages/Profile";
import CreateEvent from "./pages/CreateEvent";
import Auth from "./pages/Auth";
import Vote from "./pages/Vote";
import AdminPanel from "./pages/AdminPanel";
import Subscription from "./pages/Subscription";
import Help from "./pages/Help";
import Contact from "./pages/Contact";
import EventPlanningGuide from "./pages/EventPlanningGuide";
import EventPlannerPro from "./pages/EventPlannerPro";
import GuestList from "./pages/GuestList";
import CalendarCallback from "./pages/CalendarCallback";
import NotFound from "./pages/NotFound";
import { FamilyDashboard } from "./components/FamilyDashboard";
import ClientPortalPage from "./pages/ClientPortalPage";
import { ClientInvitePage } from "./pages/ClientInvitePage";
import { ClientPortalInvitePage } from "./pages/ClientPortalInvitePage";
import { ClientDashboard } from "./pages/ClientDashboard";
import { FamilyInvitePage } from "./pages/FamilyInvitePage";

const queryClient = new QueryClient();

const CapacitorAppHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const backButtonListener = CapApp.addListener('backButton', () => {
      if (location.pathname === '/' || location.pathname === '/dashboard') {
        CapApp.exitApp();
      } else {
        navigate(-1);
      }
    });

    const appUrlOpenListener = CapApp.addListener('appUrlOpen', async (event: any) => {
      try {
        console.log('[Capacitor] App opened with URL:', event.url);
        
        let urlObj;
        try {
          urlObj = new URL(event.url);
        } catch (e) {
          // Fallback for custom schemes like com.remarkt.eventdatepicker://
          const normalizedUrl = event.url.replace('com.remarkt.eventdatepicker://', 'https://com.remarkt.eventdatepicker/');
          urlObj = new URL(normalizedUrl);
        }
        
        let path = urlObj.pathname + urlObj.search + urlObj.hash;
        if (path.startsWith('/localhost')) {
          path = path.substring(10);
        }
        
        // Handle OAuth implicit flow redirect
        if (event.url.includes('access_token') && event.url.includes('refresh_token')) {
          const hashParams = urlObj.hash ? new URLSearchParams(urlObj.hash.substring(1)) : null;
          const searchParams = urlObj.searchParams;
          
          const accessToken = (hashParams && hashParams.get('access_token')) || searchParams.get('access_token');
          const refreshToken = (hashParams && hashParams.get('refresh_token')) || searchParams.get('refresh_token');
          
          if (accessToken && refreshToken) {
            console.log('[Capacitor] Restoring Supabase session from OAuth deep link...');
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            if (error) {
              console.error('[Capacitor] Failed to restore session:', error);
            } else {
              console.log('[Capacitor] Session restored successfully!');
            }
          }
          const cleanPath = urlObj.pathname && urlObj.pathname !== '/' ? urlObj.pathname : '/dashboard';
          navigate(cleanPath);
        } 
        // Handle OAuth PKCE flow redirect
        else if (event.url.includes('code=')) {
          const code = urlObj.searchParams.get('code');
          if (code) {
            console.log('[Capacitor] Exchanging OAuth code for Supabase session...');
            const { error } = await supabase.auth.exchangeCodeForSession(code);
            if (error) {
              console.error('[Capacitor] Failed to exchange code for session:', error);
            } else {
              console.log('[Capacitor] Session exchanged successfully!');
            }
          }
          const cleanPath = urlObj.pathname && urlObj.pathname !== '/' ? urlObj.pathname : '/dashboard';
          navigate(cleanPath);
        } 
        // Handle regular deep links
        else if (path) {
          navigate(path);
        }
        
        // Close custom tab browser overlay
        try {
          const { Browser } = await import('@capacitor/browser');
          await Browser.close();
        } catch (e) {
          // ignore
        }
      } catch (err) {
        console.error('Error handling deep link URL:', err);
      }
    });

    return () => {
      backButtonListener.then(h => h.remove());
      appUrlOpenListener.then(h => h.remove());
    };
  }, [location.pathname, navigate]);

  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <CapacitorAppHandler />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/event/:eventId" element={<EventDetail />} />
          <Route path="/event-detail/:eventId" element={<EventDetailOwner />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/global-analytics" element={<GlobalAnalytics />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/create-event" element={<CreateEvent />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/vote/:shareableLink" element={<Vote />} />
          <Route path="/admin" element={<AdminPanel />} />
        <Route path="/subscription" element={<Subscription />} />
        <Route path="/family" element={<FamilyDashboard />} />
          <Route path="/guest-list" element={<GuestList />} />
          <Route path="/help" element={<Help />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/event-planning-guide" element={<EventPlanningGuide />} />
          <Route path="/event-planner-pro" element={<EventPlannerPro />} />
          <Route path="/calendar-callback" element={<CalendarCallback />} />
          <Route path="/client-portal/:sessionToken" element={<ClientPortalPage />} />
          <Route path="/client-invite/:token" element={<ClientInvitePage />} />
          <Route path="/client-portal-invite/:clientId" element={<ClientPortalInvitePage />} />
          <Route path="/family-invite/:invitationId" element={<FamilyInvitePage />} />
          <Route path="/client-dashboard" element={<ClientDashboard />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
